import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useUser } from '../../context/UserContext';
import { recapturaMatriz } from '../../api/apiConfig';
import { CATEGORIAS, fmtMM, pillClass, unwrap } from './recapturaUtils';

const RecapturaMatriz = () => {
  const { user } = useUser();
  const [rows, setRows] = useState([]);
  const [campana, setCampana] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [etapa, setEtapa] = useState('');

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const resp = unwrap(await recapturaMatriz(user));
        if (cancelled) return;
        if (resp.ok === false) throw new Error(resp.error || 'Error al cargar matriz');
        setRows(Array.isArray(resp.data) ? resp.data : []);
        setCampana(resp.campana || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudo cargar la matriz');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const etapas = useMemo(
    () => [...new Set(rows.map((r) => r.etapa).filter(Boolean))].sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (cat && r.categoria !== cat) return false;
      if (etapa && r.etapa !== etapa) return false;
      if (!term) return true;
      const blob = `${r.cliente || ''} ${r.rut || ''} ${r.ejecutivo || ''} ${r.competidor || ''}`.toLowerCase();
      return blob.includes(term);
    });
  }, [rows, q, cat, etapa]);

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Activación Comercial — Matriz</h1>
        <p className="text-gray-600 text-sm">
          {campana
            ? `${campana.nombre} · ${campana.fec_ini} → ${campana.fec_fin}`
            : 'Sin campaña activa. Carga un Excel desde el menú Carga.'}
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <input
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[220px]"
          placeholder="Buscar cliente, RUT o ejecutivo"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm" value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Todas las categorías</option>
          {CATEGORIAS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm" value={etapa} onChange={(e) => setEtapa(e.target.value)}>
          <option value="">Todos los estados</option>
          {etapas.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-600">
          <AiOutlineLoading3Quarters className="animate-spin" /> Cargando…
        </div>
      )}
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      {!loading && !error && (
        <div className="seccion-datos overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-left text-xs text-slate-600">
                <th className="p-3">Cliente / ejecutivo</th>
                <th className="p-3">Categoría</th>
                <th className="p-3">Cesiones IF</th>
                <th className="p-3">Bancos</th>
                <th className="p-3">Otros factoring</th>
                <th className="p-3">Competencia</th>
                <th className="p-3">Último comentario</th>
                <th className="p-3">Estado</th>
                <th className="p-3 whitespace-nowrap">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-gray-500">Sin coincidencias</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-semibold text-gray-800">{r.cliente || '—'}</div>
                    <div className="text-xs text-gray-500">{r.rut} · {r.ejecutivo || 'Sin ejecutivo'}</div>
                  </td>
                  <td className="p-3">{r.categoria}</td>
                  <td className="p-3 whitespace-nowrap">{fmtMM(r.cesiones_if)}</td>
                  <td className="p-3 whitespace-nowrap">{fmtMM(r.cesiones_banco)}</td>
                  <td className="p-3 whitespace-nowrap">{fmtMM(r.cesiones_otros)}</td>
                  <td className="p-3">{r.competidor || '—'}</td>
                  <td className="p-3">
                    <div className="text-xs text-gray-500">{r.fecha_ultimo_comentario || ''}</div>
                    <div className="line-clamp-2">{r.ultimo_comentario || '—'}</div>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${pillClass(r.etapa)}`}>
                      {r.etapa}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <Link
                      to={`/recaptura/ficha/${encodeURIComponent(r.rut)}`}
                      state={{ clienteId: r.id }}
                      className="btn-primary inline-flex items-center justify-center text-xs py-1.5 px-3 whitespace-nowrap"
                      style={{ textDecoration: 'none' }}
                    >
                      Ver ficha
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecapturaMatriz;
